import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";

type ArticleAction = "impression" | "clicked" | "loaded";

interface UseArticleAnalyticsProps {
    category: string;
    componentType: string;
    articleLabel: string;
    position: number;
    isSponsored: boolean;
    isPinned: boolean;
    franchise: string | null;
    gender: string;
    contentType: string;
}

interface UseArticleAnalyticsResults {
    containerRef: RefObject<HTMLDivElement>;
    sendClickEvent: (action: string) => void;
}

export function useArticleAnalytics(params: UseArticleAnalyticsProps): UseArticleAnalyticsResults {
    const {
        category,
        componentType,
        articleLabel,
        position,
        isSponsored,
        isPinned,
        franchise,
        gender,
        contentType,
    } = params;

    const containerRef = useRef<HTMLDivElement>(null);
    const { isVisible } = useAboveFold(containerRef, 0.5);
    const [hasImpression, setHasImpression] = useState(false);

    const sendAnalyticsEvent = useCallback(
        (action: ArticleAction, area?: string) => {
            analytics.event(
                category,
                action,
                undefined, // label
                action !== "clicked", // nonInteraction
                {}, // customData
                "", // subType
                {}, // checkoutData
                [
                    // itemData
                    {
                        item_type: category,
                        component_type: componentType,
                        editorial_label: articleLabel,
                        position: position,
                        sponsored: isSponsored,
                        is_pinned: isPinned,
                        franchise: franchise,
                        editorial_gender: gender,
                        content_type: contentType,
                        ...(area && { area: area }),
                    },
                ],
                false, // lock
                false // truncate
            );
        },
        [
            category,
            componentType,
            articleLabel,
            position,
            isSponsored,
            isPinned,
            franchise,
            gender,
            contentType,
        ]
    );

    function sendClickEvent(area: string): void {
        sendAnalyticsEvent("clicked", area);
    }

    useEffect(() => {
        sendAnalyticsEvent("loaded");
    }, [sendAnalyticsEvent]);

    useIsomorphicLayoutEffect(() => {
        if (isVisible && !hasImpression) {
            sendAnalyticsEvent("impression");
            setHasImpression(true);
        }
    }, [isVisible, hasImpression, sendAnalyticsEvent, setHasImpression]);

    return { containerRef, sendClickEvent };
}
